//#region Util Imports

import * as _ from 'underscore'

//#endregion

import { OpOperator } from '../../../../../../models/core'
import { Activity, Enums } from '../../../../../../models/common'
import { NotificationType } from '../../../../../../models/util/email-notification-options'

import { LocalizationHelper } from '../../../../../../helpers'

import { OperatorNotificationsService } from '../../../../../../services/operator'
import { SystemService } from '../../../../../../services/system'
import { UserSessionService } from '../../../../../../services/user-session'

import { IMRBaseControlController } from '../../../../../common/base'
import { IMRTabOptions } from '../../../../../common/tabs/tab'
import { IMRTabsOptions, IMRTabsState, IMRTabsTabSelectedArgs, IMRTabsTabSelectingArgs, IMRTabsInitializedArgs } from '../../../../../common/tabs'
import { IMRConfirmationPopupOptions, IIMRConfirmationPopup, IMRConfirmationPopupResult, IMRConfirmationPopupInitializedArgs } from '../../../../../common/confirmation-popup2'

import { OperatorDetailsNotificationsPageOptions, IOperatorDetailsNotificationsPage, OperatorDetailsNotificationsPageInitializedArgs, OperatorDetailsNotificationsPageSubmitArgs } from './components/page'
import { IOperatorDetailsNotifications, OperatorDetailsNotificationsOptions } from './operator-details.notifications.interfaces'

export class OperatorDetailsNotificationsController
    extends
    IMRBaseControlController<IOperatorDetailsNotifications, OperatorDetailsNotificationsOptions>
    implements
    IOperatorDetailsNotifications {

    //#region Consts

    private get OPERATOR_DETAILS_ISSUE_NOTIFICATIONS_GRID_NAME(): string { return 'OPERATOR_DETAILS_ISSUE_NOTIFICATIONS_GRID_NAME' }
    private get OPERATOR_DETAILS_LOCATION_NOTIFICATIONS_GRID_NAME(): string { return 'OPERATOR_DETAILS_LOCATION_NOTIFICATIONS_GRID_NAME' }
    private get OPERATOR_DETAILS_ROUTE_NOTIFICATIONS_GRID_NAME(): string { return 'OPERATOR_DETAILS_ROUTE_NOTIFICATIONS_GRID_NAME' }
    private get OPERATOR_DETAILS_TASK_NOTIFICATIONS_GRID_NAME(): string { return 'OPERATOR_DETAILS_TASK_NOTIFICATIONS_GRID_NAME' }

    private get OPERATOR_DETAILS_ISSUE_NOTIFICATIONS_TAB_NAME(): string { return 'OPERATOR_DETAILS_ISSUE_NOTIFICATIONS_TAB_NAME' }
    private get OPERATOR_DETAILS_LOCATION_NOTIFICATIONS_TAB_NAME(): string { return 'OPERATOR_DETAILS_LOCATION_NOTIFICATIONS_TAB_NAME' }
    private get OPERATOR_DETAILS_ROUTE_NOTIFICATIONS_TAB_NAME(): string { return 'OPERATOR_DETAILS_ROUTE_NOTIFICATIONS_TAB_NAME' }
    private get OPERATOR_DETAILS_TASK_NOTIFICATIONS_TAB_NAME(): string { return 'OPERATOR_DETAILS_TASK_NOTIFICATIONS_TAB_NAME' }

    //#endregion


    //#region Members

    public isLoading: boolean
    private operator: OpOperator

    //#endregion


    //#region Ctor

    public constructor(
        private operatorNotificationsService: OperatorNotificationsService,
        private systemService: SystemService,
        private userSessionService: UserSessionService
    ) {
        super()
    }

    //#endregion


    //#region Component

    protected initialize(options: OperatorDetailsNotificationsOptions): JQueryPromise<void> {
        this.isLoading = true

        return $
            .when(
                this.initializeConfirmationPopup(),
                this.initializeTabs(),
                this.refresh(options.operator)
            )
            .then(() => {
                this.isLoading = false
            })
    }

    private onOperatorSaved(savedOperator: OpOperator) {
        super.callEvent(this.options.onOperatorSaved, {
            operator: savedOperator
        })

        return this.refresh(savedOperator)
    }

    private refresh(operator: OpOperator) {
        this.operator = operator
    }

    private submit(operator: OpOperator): JQueryPromise<void> {
        this.systemService.showLoadIndicator()

        return this.operatorNotificationsService
            .saveNotificationsToOperator(this.operator, operator)
            .then((savedOperator: OpOperator) => {
                this.onOperatorSaved(savedOperator)
            })
            .always(() => {
                this.systemService.hideLoadIndicator()
            })
    }

    //#endregion


    //#region IOperatorDetailsNotifications Implementation

    public hasChanges(): boolean {
        const hasChangesList: boolean[] = [
            this.issueNotificationPage != null ? this.issueNotificationPage.hasChanges() : false,
            this.taskNotificationPage != null ? this.taskNotificationPage.hasChanges() : false,
            this.routeNotificationPage != null ? this.routeNotificationPage.hasChanges() : false,
            this.locationNotificationPage != null ? this.locationNotificationPage.hasChanges() : false,
        ]

        return _.some(hasChangesList, (hasChanges: boolean) => hasChanges)
    }

    //#endregion


    //#region Confirmation Popup

    public confirmationPopupOptions: IMRConfirmationPopupOptions
    private confirmationPopup: IIMRConfirmationPopup

    private initializeConfirmationPopup(): JQueryPromise<any> {
        this.confirmationPopupOptions = {
            content: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AreYouSure),
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.UnsavedChanges),

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            }
        }

        return super.waitForOnInitialized(this.confirmationPopupOptions)
    }

    //#endregion


    //#region Issue

    public issueNotificationPageOptions: OperatorDetailsNotificationsPageOptions
    private issueNotificationPage: IOperatorDetailsNotificationsPage

    private initializeIssueNotificationPage(): JQueryPromise<any> {
        this.issueNotificationPageOptions = {
            gridName: this.OPERATOR_DETAILS_ISSUE_NOTIFICATIONS_GRID_NAME,
            notificationType: NotificationType.Issue,
            operator: this.operator,
            allowColumnReordering: !this.userSessionService
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OperatorNotificationIssue),

            onInitialized: (e: OperatorDetailsNotificationsPageInitializedArgs) => {
                this.issueNotificationPage = e.component
            },

            onSubmit: (e: OperatorDetailsNotificationsPageSubmitArgs) => {
                this.submit(e.operator)
                    .then(() => {
                        this.issueNotificationPage.setOperator(this.operator)
                        this.issueNotificationPage.reset()
                    })
            }
        }

        return super.waitForOnInitialized(this.issueNotificationPageOptions)
    }

    private clearIssueNotificaitons(): void {
        this.issueNotificationPage = null
    }

    //#endregion


    //#region Task

    public taskNotificationPageOptions: OperatorDetailsNotificationsPageOptions
    private taskNotificationPage: IOperatorDetailsNotificationsPage

    private initializeTaskNotificationPage(): JQueryPromise<any> {
        this.taskNotificationPageOptions = {
            gridName: this.OPERATOR_DETAILS_TASK_NOTIFICATIONS_GRID_NAME,
            notificationType: NotificationType.Task,
            operator: this.operator,
            allowColumnReordering: !this.userSessionService
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OperatorNotificationTask),

            onInitialized: (e: OperatorDetailsNotificationsPageInitializedArgs) => {
                this.taskNotificationPage = e.component
            },

            onSubmit: (e: OperatorDetailsNotificationsPageSubmitArgs) => {
                this.submit(e.operator)
                    .then(() => {
                        this.taskNotificationPage.setOperator(this.operator)
                        this.taskNotificationPage.reset()
                    })
            }
        }

        return super.waitForOnInitialized(this.taskNotificationPageOptions)
    }

    private clearTaskNotificaitons(): void {
        this.taskNotificationPage = null
    }

    //#endregion


    //#region Route

    public routeNotificationPageOptions: OperatorDetailsNotificationsPageOptions
    private routeNotificationPage: IOperatorDetailsNotificationsPage

    private initializeRouteNotificationPage(): JQueryPromise<any> {
        this.routeNotificationPageOptions = {
            gridName: this.OPERATOR_DETAILS_ROUTE_NOTIFICATIONS_GRID_NAME,
            notificationType: NotificationType.WorkOrder,
            operator: this.operator,
            allowColumnReordering: !this.userSessionService
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OperatorNotificationRoute),

            onInitialized: (e: OperatorDetailsNotificationsPageInitializedArgs) => {
                this.routeNotificationPage = e.component
            },

            onSubmit: (e: OperatorDetailsNotificationsPageSubmitArgs) => {
                this.submit(e.operator)
                    .then(() => {
                        this.routeNotificationPage.setOperator(this.operator)
                        this.routeNotificationPage.reset()
                    })
            }
        }

        return super.waitForOnInitialized(this.routeNotificationPageOptions)
    }

    private clearRouteNotificaitons(): void {
        this.routeNotificationPage = null
    }

    //#endregion


    //#region Location

    public locationNotificationPageOptions: OperatorDetailsNotificationsPageOptions
    private locationNotificationPage: IOperatorDetailsNotificationsPage

    private initializeLocationNotificationPage(): JQueryPromise<any> {
        this.locationNotificationPageOptions = {
            gridName: this.OPERATOR_DETAILS_LOCATION_NOTIFICATIONS_GRID_NAME,
            notificationType: NotificationType.Location,
            operator: this.operator,
            allowColumnReordering: !this.userSessionService
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OperatorNotificationLocation),

            onInitialized: (e: OperatorDetailsNotificationsPageInitializedArgs) => {
                this.locationNotificationPage = e.component
            },

            onSubmit: (e: OperatorDetailsNotificationsPageSubmitArgs) => {
                this.submit(e.operator)
                    .then(() => {
                        this.locationNotificationPage.setOperator(this.operator)
                        this.locationNotificationPage.reset()
                    })
            }
        }

        return super.waitForOnInitialized(this.locationNotificationPageOptions)
    }

    private clearLocationNotificaitons(): void {
        this.locationNotificationPage = null
    }

    //#endregion


    //#region Tabs

    public notificationsIssuesTabOptions: IMRTabOptions = null
    public notificationsLocationsTabOptions: IMRTabOptions = null
    public notificationsRoutesTabOptions: IMRTabOptions = null
    public notificationsTabsOptions: IMRTabsOptions = null
    public notificationsTasksTabOptions: IMRTabOptions = null

    private initializeTabs(): void {
        this.notificationsTabsOptions = {
            tabsCSSClass: 'operators-details__small-tabs',

            onStateStoring: (state: IMRTabsState) => {
                this.operatorNotificationsService.saveTabState(state)
            },

            onStateRestoring: () => {
                return this.operatorNotificationsService.restoreTabState()
            },

            onInitialized: (e: IMRTabsInitializedArgs) => {
                this.initializeTab(e.component.currentTabName)
            },

            onTabSelecting: (e: IMRTabsTabSelectingArgs) => {
                if (this.hasChanges()) {
                    e.cancelAsync = this.confirmationPopup
                        .show()
                        .then((result: IMRConfirmationPopupResult) => {
                            return result.canceled || result.declined
                        })
                }
            },

            onTabSelected: (e: IMRTabsTabSelectedArgs) => {
                this.initializeTab(e.currentTabName)
                this.clearTab(e.previousTabName)
            }
        }

        this.notificationsIssuesTabOptions = {
            name: this.OPERATOR_DETAILS_ISSUE_NOTIFICATIONS_TAB_NAME,
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Issues),
        }

        this.notificationsTasksTabOptions = {
            name: this.OPERATOR_DETAILS_TASK_NOTIFICATIONS_TAB_NAME,
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Tasks),
        }

        this.notificationsRoutesTabOptions = {
            name: this.OPERATOR_DETAILS_ROUTE_NOTIFICATIONS_TAB_NAME,
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Routes),
        }

        this.notificationsLocationsTabOptions = {
            name: this.OPERATOR_DETAILS_LOCATION_NOTIFICATIONS_TAB_NAME,
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Locations),
        }
    }

    private initializeTab(tabName: string): void {
        switch (tabName) {
            case this.OPERATOR_DETAILS_ISSUE_NOTIFICATIONS_TAB_NAME:
                this.initializeIssueNotificationPage()
                break

            case this.OPERATOR_DETAILS_TASK_NOTIFICATIONS_TAB_NAME:
                this.initializeTaskNotificationPage()

                break

            case this.OPERATOR_DETAILS_ROUTE_NOTIFICATIONS_TAB_NAME:
                this.initializeRouteNotificationPage()

                break

            case this.OPERATOR_DETAILS_LOCATION_NOTIFICATIONS_TAB_NAME:
                this.initializeLocationNotificationPage()

                break
        }
    }

    private clearTab(tabName: string): void {
        switch (tabName) {
            case this.OPERATOR_DETAILS_ISSUE_NOTIFICATIONS_TAB_NAME:
                this.clearIssueNotificaitons()
                break

            case this.OPERATOR_DETAILS_TASK_NOTIFICATIONS_TAB_NAME:
                this.clearTaskNotificaitons()

                break

            case this.OPERATOR_DETAILS_ROUTE_NOTIFICATIONS_TAB_NAME:
                this.clearRouteNotificaitons()

                break

            case this.OPERATOR_DETAILS_LOCATION_NOTIFICATIONS_TAB_NAME:
                this.clearLocationNotificaitons()

                break
        }
    }

    //#endregion
}
